import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import DownloadIcon from '@mui/icons-material/DownloadOutlined'
import AddIcon from '@mui/icons-material/Add'
import InfoIcon from '@mui/icons-material/InfoOutlined'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Search from 'components/Search'
import KeyboardAccessible from 'components/KeyboardAccessible'

import AlertCodeList from 'pages/AlertLookup/AlertCodeList'
import AlertDescriptionModal from 'pages/shared/AlertDescriptionModal'
import AlertDescriptionsModal from 'pages/AlertLookup/AlertDescriptionsModal'
import AdminAlertDescriptionModal from 'pages/AlertLookup/AdminAlertDescriptionModal'
import { searchAlertDescriptions, searchConsolidatedEventsDescriptions } from 'pages/AlertLookup/search'
import ConsolidatedEventsList from 'pages/AlertLookup/ConsolidatedEventList'
import ConsolidatedEventModal from 'pages/AlertLookup/ConsolidatedEventModal'
import NotFound from 'pages/NotFound'

import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import useAlertDescriptions from 'hooks/useAlertDescriptions'
import useSearchParams from 'hooks/useSearchParams'
import useConsolidatedEvents from 'hooks/useConsolidatedEvents'

import GA4 from 'helpers/ga4'
import { alertCodeRawValue } from 'helpers/alerts'

import { EmptyListMessage, screenSize, LoadingSinglePanePage, PageTitle, SinglePanePage } from 'styles/shared'
import { color } from 'styles/colors'

import Api from 'services/api'

import blobToFileDownload from 'helpers/blobToFileDownload'

const AlertLookup = () => {
  useAlertDescriptions()
  useConsolidatedEvents()

  const { session: { isAlarmDescriptionAdmin: isAlertDescriptionAdmin, isAdmin, companyBrand } } = useSessionContext()
  const { alertDescriptions, areAlertDescriptionsLoaded, consolidatedEvents, areConsolidatedEventsLoaded } = useStoreContext()

  const navigate = useNavigate()

  const id = useSearchParams('id')
  const query = useSearchParams('q') || ''
  const showModal = useSearchParams('showModal') === 'true'

  const [showAlertDescriptionsModal, setShowAlertDescriptionsModal] = useState(false)
  const [searchValue, setSearchValue] = useState(query)
  const [showSpinner, setShowSpinner] = useState(false)

  const filteredAlertDescriptions = query && searchAlertDescriptions(query, alertDescriptions)
  const filteredConsolidatedEvents = query && searchConsolidatedEventsDescriptions(query, consolidatedEvents)

  const sendGa4Events = () => {
    const eventData = { category: 'Alert Lookup Page', action: 'Click Download Alert Guide', label: companyBrand.toUpperCase() }
    GA4.sendEvent(eventData)
  }

  const onDownloadCsvClick = useCallback(() => {
    Api.AlertGuide.getCSV().then((response) => {
      blobToFileDownload(response, 'AlertGuide.pdf')
    })

    sendGa4Events()
  }, [companyBrand])

  const onDownloadExcelClick = useCallback(() => {
    Api.AlertGuide.getCSV().then((response) => {
      blobToFileDownload(response, 'AlertGuide.xlsx')
    })

    sendGa4Events()
  }, [companyBrand])

  const showDescriptionsModal = () => {
    setShowAlertDescriptionsModal(true)
  }

  if (!areAlertDescriptionsLoaded || !areConsolidatedEventsLoaded) {
    return <LoadingSinglePanePage />
  }

  const alertCodeOrEventExists = _.any(alertDescriptions || [], (alertDescription) => alertDescription.code === alertCodeRawValue(id)) ||
    _.any(consolidatedEvents || [], (event) => event.id === id)

  if (id && !alertCodeOrEventExists) {
    return <NotFound />
  }

  const currentAlert = _.find(alertDescriptions, (alertDescription) => (
    alertCodeRawValue(alertDescription.code) === alertCodeRawValue(id)
  ))

  const isAlertDescriptionModalDisplayed = !!id && !!currentAlert && showModal
  const onSearchQuery = (query = '') => {
    setShowSpinner(true)
    navigate(`${query ? `?q=${query}` : ''}`)
    setTimeout(() => {
      setShowSpinner(false)
    }, 2000)
  }

  const onClickClearIcon = () => {
    setShowSpinner(false)
    setSearchValue('')
    onSearchQuery('')
  }

  return (
    <SinglePanePage>
      <div>
        <S.AlertGuideGrid>
          <S.Title>Alert Lookup</S.Title>

          <S.DownloadLink
            target="_blank"
            aria-label="csv download link"
          >
            <Button decoration="textOnly" icon="download" text="Download Alert Guide" onPress={onDownloadCsvClick} />
          </S.DownloadLink>

          <S.ButtonContainer>
            {isAlertDescriptionAdmin && (
              <Button decoration="textOnly" icon="new" text="Add Alert" onPress={() => navigate(`/alerts/new${query ? `?q=${query}` : ''}`)} />
            )}

            {isAdmin &&
              <S.DownloadLink aria-label="excel download link" >
                <Button decoration="textOnly" icon="download" text="Download Excel" onPress={onDownloadExcelClick} />
              </S.DownloadLink>
            }

          </S.ButtonContainer>
        </S.AlertGuideGrid>

        <S.Search
          value={searchValue}
          placeholder="Search by alert code or description"
          ariaLabel="alert codes"
          onChange={setSearchValue}
          onSearch={onSearchQuery}
          onClickClearIcon={onClickClearIcon}
          showSpinner={showSpinner}
          autoFocus
        />

        <S.AlertDescriptionsLink
          onClick={() => showDescriptionsModal()}
        >
          <InfoIcon />
          <S.AlertDescriptionText>Alert Descriptions</S.AlertDescriptionText>
        </S.AlertDescriptionsLink>
      </div>

      {(() => {
        if (_.isEmpty(alertDescriptions) && _.isEmpty(consolidatedEvents)) {
          return <EmptyListMessage>NO RESULTS</EmptyListMessage>
        } else if (!query) {
          return <></>
        } else if (_.isEmpty(filteredAlertDescriptions) && _.isEmpty(filteredConsolidatedEvents)) {
          return <EmptyListMessage>No alerts match "{query}".</EmptyListMessage>
        } else {
          return (
            <>
              {!!query && <S.FilteredByMessage>Results are filtered by the search criteria "{query}".</S.FilteredByMessage>}
              {!_.isEmpty(filteredAlertDescriptions) && <AlertCodeList alertDescriptions={filteredAlertDescriptions} query={query} />}
              {!_.isEmpty(filteredConsolidatedEvents) && <ConsolidatedEventsList consolidatedEvents={filteredConsolidatedEvents} query={query} />}
            </>
          )
        }
      })()}

      <ConsolidatedEventModal query={query} />
      {isAlertDescriptionAdmin
        ? <AdminAlertDescriptionModal
          query={query}
          isDisplayed={isAlertDescriptionModalDisplayed}
          alertDescription={currentAlert}
        />
        : <AlertDescriptionModal
          isDisplayed={isAlertDescriptionModalDisplayed}
          alertDescription={currentAlert}
          closeCallback={() => { navigate(`/alerts?q=${query}&id=${alertCodeRawValue(id)}`) }}
        />
      }
      <AlertDescriptionsModal isVisible={showAlertDescriptionsModal} closeCallback={() => setShowAlertDescriptionsModal(false)} />
    </SinglePanePage>
  )
}

export default AlertLookup

const S = {
  DownloadIcon: styled(DownloadIcon)`
    margin-bottom: -5px;
  `,
  DownloadLink: styled.a`
      margin: 5px 15px;
      display: inline-block;
  `,
  AddIcon: styled(AddIcon)`
    margin-bottom: -5px;
  `,
  AlertDescriptionText: styled.span`
    margin-left: 5px;
  `,
  AlertDescriptionsLink: styled(KeyboardAccessible)`
    margin: 0 10px;
    padding: 5px 8px;
    color: ${color('alertDescriptions')};
    cursor: pointer;
    font-weight: bold;
    margin-left: -7px;
    display: flex;
    width: 167px;

    &:hover {
      color: ${color('alertDescriptionsHover')};
    }
  `,
  Search: styled(Search)`
    width: 395px;
    margin: 15px 0;

    @media ${screenSize.small} {
      display: block;
      width: 100%;
    }
  `,
  FilteredByMessage: styled.div`
    margin: 20px 0 40px 0;
    color: ${color('filteredByMessage')};
    font-size: 1.5em;
  `,
  Title: styled(PageTitle)`
    display: inline-block;
    margin-right: 20px;

    @media ${screenSize.small} {
      display: none;
    }
  `,
  AlertGuideGrid: styled.div`
    display: flex;
    flex-direction: column;
  `,
  ButtonContainer: styled.div`
    display: flex;
    flex-direction: row;
  `
}
